import React, { useState } from "react";
import { ChevronDown, HelpCircle, MapPin } from "lucide-react";
import { CIMA, openWhatsapp } from "@/lib/cima";

/**
 * Lightweight FAQ section with semantic content targeting real search queries
 * from Search Console: "gas zihuatanejo", "infra zihuatanejo", "tanque de gas",
 * "gas a domicilio cerca de mi ubicación", "venta de gas lp cerca de mi ubicación".
 *
 * Includes a polite clarification that we DO NOT sell LP gas, redirecting users
 * to the gases we do sell, capturing traffic and converting.
 */
const FAQS = [
  {
    q: "¿Venden gas LP en Zihuatanejo?",
    a: (
      <>
        <strong>No comercializamos gas LP doméstico.</strong> En{" "}
        <strong>CIMA Zihuatanejo</strong> somos especialistas en{" "}
        <strong>gases industriales y medicinales</strong>: oxígeno, nitrógeno,
        argón, CO₂, acetileno, helio y mezclas para soldadura. Si necesitas{" "}
        <strong>oxígeno medicinal</strong>, equipo para soldadura o gases
        especializados, escríbenos por WhatsApp o llama al 755 554 7990 y con
        gusto te asesoramos.
      </>
    ),
  },
  {
    q: "¿Dónde se ubica CIMA y cuál es el horario?",
    a: (
      <>
        Estamos en <strong>Paseo de Zihuatanejo Oriente #198, Col. El Hujal,
        Zihuatanejo de Azueta, Guerrero</strong>. Horario: Lun – Vie 8:00 a.m.
        – 5:30 p.m. · Sáb 8:00 a.m. – 2:00 p.m. Para{" "}
        <strong>recargas urgentes de oxígeno medicinal</strong> atendemos por
        teléfono 24/7.
      </>
    ),
  },
  {
    q: "¿Recargan tanques de oxígeno a domicilio?",
    a: (
      <>
        Sí. Hacemos <strong>recarga de tanques portátiles de oxígeno medicinal
        el mismo día y en minutos</strong>, con servicio a domicilio en{" "}
        <strong>Zihuatanejo, Ixtapa</strong> y zona conurbada para que ningún
        paciente espere.
      </>
    ),
  },
  {
    q: "¿Son distribuidores autorizados INFRA?",
    a: (
      <>
        Sí. <strong>CIMA es Distribuidor Autorizado INFRA</strong> en
        Zihuatanejo y la Costa Grande de Guerrero desde hace más de 50 años —
        fundada en 1972. Producto INFRA original con respaldo de fábrica.
      </>
    ),
  },
  {
    q: "¿Rentan tanques y concentradores de oxígeno?",
    a: (
      <>
        Sí. Rentamos <strong>tanques de oxígeno medicinal</strong> de 682 L,
        425 L, 1,724 L, 3,455 L MM y estacionarios de 9,500 L, además de{" "}
        <strong>concentradores de oxígeno de 5 L y 10 L</strong>. La renta es
        por día, según los días que necesite el paciente.
      </>
    ),
  },
  {
    q: "¿Qué gases industriales venden en Zihuatanejo?",
    a: (
      <>
        Manejamos catálogo completo de{" "}
        <strong>gases industriales en Zihuatanejo</strong>: oxígeno industrial
        (1, 2, 6 y 9.5 m³), nitrógeno, argón y argón alta pureza, CO₂,
        acetileno (1 – 4.5 kg), helio, SF₆ y mezclas INFRA MIX 75/25, 98/2 y
        200/25 para soldadura MIG.
      </>
    ),
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      aria-label="Preguntas frecuentes sobre gases industriales y medicinales en Zihuatanejo"
      className="relative bg-[var(--cima-surface)] py-20 lg:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 stripe-accent opacity-[0.04] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">
          {/* Left column: title + local SEO context */}
          <div className="lg:sticky lg:top-28 self-start">
            <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-[var(--cima-red)] flex items-center gap-2">
              <HelpCircle className="w-3.5 h-3.5" />
              Preguntas frecuentes
            </span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[var(--cima-navy)] mt-3 leading-[1.05]">
              Gas en Zihuatanejo,
              <br />
              <span className="text-[var(--cima-red)]">respuestas claras.</span>
            </h2>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
              Todo lo que necesitas saber sobre nuestros gases industriales y
              medicinales, distribución INFRA, recargas a domicilio y renta de
              equipos en Zihuatanejo e Ixtapa.
            </p>

            <div className="mt-6 inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-2 text-xs">
              <MapPin className="w-3.5 h-3.5 text-[var(--cima-red)]" />
              <span className="text-slate-700 font-medium">
                Zihuatanejo · Ixtapa · Costa Grande Gro.
              </span>
            </div>
          </div>

          {/* Right column: accordion */}
          <div className="space-y-3">
            {FAQS.map((item, idx) => {
              const open = openIdx === idx;
              return (
                <div
                  key={idx}
                  data-testid={`faq-item-${idx}`}
                  className={`border bg-white transition-colors ${
                    open
                      ? "border-[var(--cima-red)] shadow-md"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <button
                    type="button"
                    data-testid={`faq-toggle-${idx}`}
                    onClick={() => setOpenIdx(open ? -1 : idx)}
                    className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
                    aria-expanded={open}
                  >
                    <h3 className="font-display font-bold text-base lg:text-lg text-[var(--cima-navy)] leading-snug">
                      {item.q}
                    </h3>
                    <ChevronDown
                      className={`w-5 h-5 shrink-0 transition-transform ${
                        open ? "rotate-180 text-[var(--cima-red)]" : "text-slate-400"
                      }`}
                    />
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                        {item.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="mt-6 bg-[var(--cima-navy)] text-white p-6 lg:p-7">
              <p className="font-display font-semibold text-base lg:text-lg leading-snug">
                ¿Tienes otra pregunta o necesitas una cotización?
              </p>
              <p className="text-white/80 text-sm mt-1.5">
                Nuestro equipo te responde en minutos por WhatsApp.
              </p>
              <button
                type="button"
                data-testid="faq-whatsapp-cta"
                onClick={() =>
                  openWhatsapp(
                    "Hola CIMA, tengo una consulta sobre sus gases / servicios."
                  )
                }
                className="mt-4 inline-flex items-center gap-2 bg-[var(--cima-red)] hover:bg-[var(--cima-red-deep)] px-5 py-3 font-display font-bold text-sm uppercase tracking-wide transition-colors"
              >
                Escribir a WhatsApp · {CIMA.whatsapp}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
